import { Injectable } from '@angular/core';
import { AiWorkspaceSuggestions } from './ai-workspace-setup.models';
import { AiSetupAssistantService } from '../../ai-setup-assistant/ai-setup-assistant.service';

const WEBSITE_EXTRACT_PROMPT = `You are a business intelligence assistant. Given a business website URL (and optionally other listing URLs), search the web for information about this business and extract key details. Return ONLY a valid JSON object (no markdown, no explanation, no surrounding text).

The JSON must have this exact structure:
{
  "businessName": "<the business/company name>",
  "businessDescription": "<2-3 sentence description of what the business does, its focus areas, and target market>",
  "industry": "<inferred industry: generic|loan_dsa|edtech|real_estate|travel|legal|solar|installations|other>",
  "logoUrl": "<URL of the business logo if found, or null>",
  "products": ["<list of main products or services offered>"],
  "location": "<city/region if identifiable>",
  "tagline": "<business tagline or slogan if found, or null>"
}

Guidelines:
- Extract as much detail as possible from the website content
- The businessDescription should be informative enough to configure a CRM (mention what they sell, who their customers are, their sales model)
- If multiple URLs are provided, cross-reference information from all of them
- If a URL is not accessible, infer what you can from the domain name and any search results

LOGO EXTRACTION (critical — read carefully):
- You MUST find the actual VISIBLE business logo that users see on the webpage.
- LOOK FOR: The main logo <img> tag in the website's header, navbar, or hero section. Check the src attribute of <img> tags where:
  - The alt, class, or id contains "logo" (e.g. class="site-logo", alt="Company Logo")
  - The image is inside a <header>, <nav>, or <a> linking to the homepage
  - The filename itself contains the business name (e.g. "PhincoElite.png", "companyname-logo.svg")
- ALSO CHECK: <meta property="og:image">, Schema.org JSON-LD "logo" field, footer logos
- NEVER return favicon/icon URLs. Reject any URL containing: "favicon", "icon", "apple-touch", "cropped-favicon", "site-icon", or any <link rel="icon"> / <link rel="shortcut icon"> source.
- The logoUrl MUST be a full absolute URL (starting with https://) pointing directly to an image file (.png, .jpg, .webp, .svg)
- NEVER return image proxy/optimizer URLs like "/_next/image?url=..." or "?w=384&q=75" URLs. Instead, extract the actual source image path.
- Prefer PNG/WebP/SVG over JPG. Prefer higher resolution.
- Return null for logoUrl ONLY if you truly cannot find any non-favicon logo image`;

const MAIN_SYSTEM_PROMPT = `You are a CRM setup assistant. Based on the business description, generate customization suggestions for a sales CRM. Return ONLY a valid JSON object (no markdown, no explanation, no surrounding text).

The JSON must have this exact structure:
{
  "industry": "<inferred industry: generic|loan_dsa|edtech|real_estate|travel|legal|solar|installations|other>",
  "lifecycle_names": {
    "UNCONTACTED_NEW": "<custom name for new leads not yet worked on (default: New Lead)>",
    "UNCONTACTED_RECHURNED": "<custom name for re-engaged/rechurned leads being reworked (default: Rechurned Lead)>",
    "PROSPECTING": "<custom name for leads being prospected/pre-qualification (default: Prospecting Lead)>",
    "OPPORTUNITY": "<custom name for qualified leads where a deal is created (default: Opportunity Lead)>",
    "CLOSED": "<custom name for leads that reached final closure — won, lost, or inactive (default: Closed Lead)>"
  },
  "call_outcomes": [
    { "name": "<outcome name>", "nature": "positive|negative|neutral", "startsNewDeal": true/false, "junkLead": true/false }
  ],
  "visit_outcomes": [
    { "name": "<visit outcome name>", "nature": "positive|negative|neutral" }
  ],
  "deal_stages": [
    { "name": "<stage name>", "description": "<1 line description>", "isTerminalSuccess": true/false, "isTerminalFailure": true/false }
  ],
  "products": [
    { "name": "<product or service name>", "type": "PRODUCT|SERVICE", "category": "<optional category>" }
  ],
  "custom_contact_properties": [
    { "name": "<property display name>", "type": "Text|Number|Option|DateTime", "options": ["<only if type is Option>"] }
  ],
  "work_pipeline_items": [
    { "title": "<work type name>", "actionLabel": "<Call|WhatsApp|Email|Follow Up|Review>", "priority": 1 }
  ]
}

NAMING RULE: All names (outcomes, stages, products, properties, pipeline items) must be SHORT — 2-4 words max. Easy to read at a glance by sales reps. No long descriptions in the name field. Examples: "Interested", "Site Visit Done", "Send Quotation", "Budget Range".

Guidelines:

LIFECYCLE NAMES:
- These are the 5 fixed stages every lead goes through. You CANNOT add or remove stages — only customize display names.
- UNCONTACTED_NEW: Lead has just entered the system and has never been called or contacted. Fresh/new lead.
- UNCONTACTED_RECHURNED: A previously closed or dormant lead that has been re-opened for a second attempt. Re-engaged lead.
- PROSPECTING: Lead has been contacted (called/messaged) and is being actively worked on, but not yet qualified. In early conversations.
- OPPORTUNITY: Lead has shown interest, a deal has been created, and active negotiation/sales process is underway. Qualified lead. Within this stage, "deal stages" (configured separately) act as finer sub-stages tracking the deal's progress (e.g. Quotation Sent → Negotiation → PO Received).
- CLOSED: Lead has reached a final state — either won (converted to customer) or lost (rejected/gone cold). Terminal state.
- Use industry-specific terminology. E.g. for real estate: "New Inquiry Lead", "Re-engaged Lead", "Site Visit Lead", "Booking Lead", "Closed Lead".
- Always end each name with "Lead".

CALL OUTCOMES:
- These are ONLY for CONNECTED calls — outcomes after a salesperson actually speaks with the lead.
- The system already handles unconnected scenarios (No Answer, Switched Off, Invalid Number, etc.) separately. Do NOT include any unconnected-call outcomes.
- Generate 5-7 connected-call outcomes: at least 2 positive, 2 negative, 1-2 neutral
- One positive outcome MUST be an "Interested" variant with startsNewDeal: true
- One negative outcome MUST be a "Not Interested" or junk variant with junkLead: true
- Good examples: "Interested", "Interested - Future", "Send More Info", "Call Back Later", "Not Interested", "Wrong Number", "Existing Customer"
- Keep names simple — a rep should understand the outcome instantly

VISIT OUTCOMES:
- Generate 4-6 visit outcomes for field visits: at least 1 positive, 1 negative, 1 neutral
- These are outcomes when a salesperson visits a client in person
- Good examples: "Client Interested", "Needs Follow-up", "Not Available", "Deal Closed", "Location Not Found"

DEAL STAGES:
- Should reflect THIS business's actual sales process, not generic CRM stages
- For an automation company: "Site Survey", "Quotation Sent", "Negotiation", "PO Received", "Installation Done"
- For real estate: "Site Visit", "Negotiation", "Booking", "Registration"
- For healthcare/clinic: "Consultation", "Plan Proposed", "Decision Pending", "Treatment Booked", "Deal Lost"
- Generate 4-7 stages. The first stage = initial/prospect stage.
- EXACTLY one stage must have isTerminalSuccess: true (the "Won" stage — e.g. "Deal Closed", "Treatment Booked", "Registration Done")
- EXACTLY one stage must have isTerminalFailure: true (the "Lost" stage — e.g. "Deal Lost", "Not Going Ahead"). Combine all loss reasons into ONE stage.
- All other stages must have isTerminalSuccess: false AND isTerminalFailure: false
- Keep stage names short (2-4 words). Put details in the description field, not the name.

PRODUCTS:
- If known products/services are provided in the input, use them directly. Categorize appropriately. You may add a few more relevant ones.
- Generate 3-8 products or services relevant to this business

CUSTOM CONTACT PROPERTIES:
- Should be fields a salesperson needs when qualifying a lead for THIS specific business
- For an automation company: "Site Type", "Gate Type", "Number of Gates", "Gate Width"
- For real estate: "Budget Range", "BHK Preference", "Possession Timeline"
- Make them SPECIFIC to the business, not generic
- Generate 3-6 properties. Do NOT suggest properties that overlap with these built-in system properties: Lead Source, Lead Lifecycle, Deal Stage, Lead Tag, Name, Department, Designation, City, Product/Service Interest

WORK PIPELINE:
- Generate 3-5 work pipeline items sorted by priority (1 = highest)
- Keep titles short and action-oriented

OTHER:
- The input may include: business description, industry, location, and known products/services. Use ALL of this context.
- For lifecycle_names, see the LIFECYCLE NAMES section above. Use the exact enum keys provided.`;

@Injectable({ providedIn: 'root' })
export class AiWorkspaceSetupService {

  private shared = new AiSetupAssistantService();

  async extractBusinessFromWebsite(websiteUrl: string, additionalUrls?: string[]): Promise<any> {
    const urls = [websiteUrl, ...(additionalUrls || [])].filter(u => u?.trim());
    const urlList = urls.map(u => `- ${u.trim()}`).join('\n');

    console.log('[AI Workspace Setup] Extracting business info from:', urls);

    const parsed = await this.shared.callAi(
      'aiWebsiteExtract',
      `Extract business information from these URLs:\n${urlList}\n\nSearch the web for these URLs and return structured business information as JSON.`,
      WEBSITE_EXTRACT_PROMPT,
    );

    // Post-process: reject favicon/icon URLs that AI may still return
    let logoUrl = parsed.logoUrl || null;
    if (logoUrl) {
      const lower = logoUrl.toLowerCase();
      const isFavicon = /favicon|site-icon|apple-touch|cropped-favicon/i.test(lower)
        || /\bicon\b/.test(lower.split('/').pop());
      if (isFavicon) {
        console.log('[AI Workspace Setup] Rejected favicon URL from AI:', logoUrl);
        logoUrl = null;
      }
    }

    return {
      businessName: parsed.businessName || '',
      businessDescription: parsed.businessDescription || '',
      industry: parsed.industry || 'generic',
      logoUrl,
      products: Array.isArray(parsed.products) ? parsed.products : [],
      location: parsed.location || '',
      tagline: parsed.tagline || null,
    };
  }

  async generateAllSuggestions(businessDescription: string, extractedData?: any): Promise<AiWorkspaceSuggestions> {
    const contextParts: string[] = [];
    contextParts.push(`Business Description: ${businessDescription}`);

    if (extractedData) {
      if (extractedData.industry && extractedData.industry !== 'generic') {
        contextParts.push(`Industry: ${extractedData.industry}`);
      }
      if (extractedData.location) {
        contextParts.push(`Location: ${extractedData.location}`);
      }
      if (extractedData.tagline) {
        contextParts.push(`Tagline: ${extractedData.tagline}`);
      }
      if (Array.isArray(extractedData.products) && extractedData.products.length > 0) {
        contextParts.push(`Known Products/Services: ${extractedData.products.join(', ')}`);
      }
    }

    const fullPrompt = contextParts.join('\n');
    console.log('[AI Workspace Setup] Generating suggestions with context:', fullPrompt);

    const parsed = await this.shared.callAi('aiWorkspaceSetup', fullPrompt, MAIN_SYSTEM_PROMPT);
    return this.validateAndNormalize(parsed);
  }

  async regenerateSection(
    section: string,
    businessDescription: string,
    previousSuggestions: any,
    feedback: string,
    extractedData?: any
  ): Promise<any> {
    const sectionPrompt = `You are a CRM setup assistant. Regenerate ONLY the "${section}" section based on user feedback. Return ONLY a valid JSON object with just the "${section}" key and its value. No markdown, no explanation.`;

    const contextParts: string[] = [];
    contextParts.push(`Business: ${businessDescription}`);
    if (extractedData) {
      if (extractedData.industry) contextParts.push(`Industry: ${extractedData.industry}`);
      if (extractedData.location) contextParts.push(`Location: ${extractedData.location}`);
      if (Array.isArray(extractedData.products) && extractedData.products.length) {
        contextParts.push(`Products/Services: ${extractedData.products.join(', ')}`);
      }
    }
    contextParts.push(`Previous suggestions for ${section}: ${JSON.stringify(previousSuggestions)}`);
    contextParts.push(`User feedback: ${feedback}`);
    contextParts.push(`Regenerate ONLY the ${section} section based on this feedback. Return valid JSON with just the "${section}" key.`);

    console.log('[AI Workspace Setup] Regenerating section:', section, 'with feedback:', feedback);

    const parsed = await this.shared.callAi('aiWorkspaceSetup', contextParts.join('\n'), sectionPrompt);

    if (parsed && parsed[section]) {
      return parsed[section];
    }
    return parsed;
  }

  async discoverTestLeads(businessDescription: string, extractedData?: any): Promise<any[]> {
    const contextParts: string[] = [];
    contextParts.push(`Business: ${businessDescription}`);
    if (extractedData) {
      if (extractedData.industry) contextParts.push(`Industry: ${extractedData.industry}`);
      if (extractedData.location) contextParts.push(`Location: ${extractedData.location}`);
      if (Array.isArray(extractedData.products) && extractedData.products.length) {
        contextParts.push(`Products/Services: ${extractedData.products.join(', ')}`);
      }
    }
    contextParts.push('Find 8-12 real businesses or individuals who would be potential CUSTOMERS of this business.');
    contextParts.push('Search the web for actual leads — real businesses/people in the same city/region who might need the products or services this business offers.');

    const systemPrompt = `You are a lead generation assistant. Given a business description, find potential CUSTOMERS for that business — NOT competitors, NOT CRM vendors. These should be real businesses or individuals who might buy the products/services this business sells.

For example:
- If the business sells hair treatments → find people searching for hair loss solutions, or clinics/salons that might refer clients
- If the business sells solar panels → find factories, warehouses, or residential complexes that might need solar
- If the business is a real estate agent → find people looking to buy/rent property in that area

Return ONLY a valid JSON array (no markdown, no explanation, no surrounding text). Each entry must have:
[
  {
    "business_name": "<name of the potential customer/business>",
    "phone": "<phone number with country code, e.g. +91XXXXXXXXXX>",
    "email": "<email if found, or null>",
    "address": "<full address>",
    "industry": "<their industry>",
    "source_url": "<where you found this info>",
    "reason": "<why they are a good lead for this business — 1 sentence>"
  }
]

Rules:
- All phone numbers must be real and include country code
- Focus on the same city/region as the business
- Leads must be potential BUYERS/CUSTOMERS of this business, not competitors
- Include a mix of business types that would need this business's services
- If you cannot find real leads with phone numbers, return an empty array []`;

    console.log('[AI Workspace Setup] Discovering test leads for:', businessDescription);

    const parsed = await this.shared.callAi('aiTestLeadDiscovery', contextParts.join('\n'), systemPrompt);

    let leads: any[];
    if (Array.isArray(parsed)) {
      leads = parsed;
    } else if (parsed && Array.isArray(parsed.leads)) {
      leads = parsed.leads;
    } else if (parsed && Array.isArray(parsed.response)) {
      leads = parsed.response;
    } else {
      console.warn('[AI Workspace Setup] Unexpected lead discovery response:', parsed);
      return [];
    }

    return leads.filter((l: any) => l.business_name && l.phone);
  }

  private validateAndNormalize(data: any): AiWorkspaceSuggestions {
    const result: AiWorkspaceSuggestions = {
      industry: data.industry || 'generic',
      lifecycle_names: data.lifecycle_names || {
        UNCONTACTED_NEW: 'New Lead',
        UNCONTACTED_RECHURNED: 'Rechurned Lead',
        PROSPECTING: 'Prospecting Lead',
        OPPORTUNITY: 'Opportunity Lead',
        CLOSED: 'Closed Lead',
      },
      call_outcomes: Array.isArray(data.call_outcomes) ? data.call_outcomes.map(o => ({
        ...o,
        selected: true,
      })) : [],
      visit_outcomes: Array.isArray(data.visit_outcomes) ? data.visit_outcomes.map(o => ({
        ...o,
        selected: true,
      })) : [],
      deal_stages: Array.isArray(data.deal_stages) ? data.deal_stages.map(s => ({
        ...s,
        selected: true,
      })) : [],
      products: Array.isArray(data.products) ? data.products.map(p => ({
        ...p,
        selected: true,
      })) : [],
      custom_contact_properties: Array.isArray(data.custom_contact_properties) ? data.custom_contact_properties.map(cp => ({
        ...cp,
        type: cp.type === 'Date' ? 'DateTime' : cp.type,
        selected: true,
      })) : [],
      work_pipeline_items: Array.isArray(data.work_pipeline_items) ? data.work_pipeline_items.map(w => ({
        ...w,
        selected: true,
      })) : [],
    };

    return result;
  }
}
